import { DotsThree, Plus, ShieldCheck } from "@phosphor-icons/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { AuthPolicy, PolicyEffect } from "~/api/governanceTypes";
import {
  useAuthPolicies,
  useCreateAuthPolicy,
  useDeleteAuthPolicy,
  useUpdateAuthPolicy,
} from "~/api/queries";
import { Pill, Row, Rows } from "~/features/automation/components";
import { ConditionsEditor } from "~/features/governance/ConditionsEditor";
import { Select, TextArea } from "~/features/settings/controls";
import { Button } from "~/foundation/components/Button";
import { ConfirmDialog, Dialog } from "~/foundation/components/Dialog";
import { Empty } from "~/foundation/components/Empty";
import { IconButton } from "~/foundation/components/IconButton";
import { Input } from "~/foundation/components/Input";
import { Menu } from "~/foundation/components/Menu";
import { Spinner } from "~/foundation/components/Spinner";
import { Switch } from "~/foundation/components/Switch";

const ACTIONS = ["read", "write", "delete", "share", "*"];

interface PolicyForm {
  name: string;
  description: string;
  resourceType: string;
  effect: PolicyEffect;
  priority: number;
  actions: string[];
  conditions: Record<string, unknown>;
  isActive: boolean;
}

function initialForm(policy?: AuthPolicy): PolicyForm {
  return {
    name: policy?.name ?? "",
    description: policy?.description ?? "",
    resourceType: policy?.resourceType ?? "*",
    effect: policy?.effect ?? "permit",
    priority: policy?.priority ?? 100,
    actions: policy?.actions ?? [],
    conditions: policy?.conditions ?? {},
    isActive: policy?.isActive ?? true,
  };
}

function PolicyDialog({ policy, onClose }: { policy?: AuthPolicy; onClose: () => void }) {
  const { t } = useTranslation();
  const createPolicy = useCreateAuthPolicy();
  const updatePolicy = useUpdateAuthPolicy();
  const [form, setForm] = useState<PolicyForm>(() => initialForm(policy));
  const isEdit = Boolean(policy);
  const busy = createPolicy.isPending || updatePolicy.isPending;

  const toggleAction = (action: string) => {
    setForm((prev) => ({
      ...prev,
      actions: prev.actions.includes(action)
        ? prev.actions.filter((a) => a !== action)
        : [...prev.actions, action],
    }));
  };

  const submit = () => {
    if (!form.name.trim()) {
      return;
    }
    const shared = {
      name: form.name.trim(),
      description: form.description.trim() || undefined,
      resourceType: form.resourceType,
      effect: form.effect,
      priority: form.priority,
      actions: form.actions,
      conditions: form.conditions,
      isActive: form.isActive,
    };
    if (isEdit && policy) {
      updatePolicy.mutate(
        { policyId: policy.id, ...shared },
        {
          onSuccess: () => {
            onClose();
            toast.success(t("Policy saved"));
          },
        },
      );
    } else {
      createPolicy.mutate(shared, {
        onSuccess: () => {
          onClose();
          toast.success(t("Policy created"));
        },
      });
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={isEdit ? t("Edit policy") : t("New policy")}
      closeLabel={t("Close")}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("Cancel")}
          </Button>
          <Button variant="primary" disabled={!form.name.trim() || busy} onClick={submit}>
            {isEdit ? t("Save") : t("Create")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Input
          label={t("Name")}
          fullWidth
          value={form.name}
          onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
        />
        <TextArea
          label={t("Description")}
          rows={2}
          value={form.description}
          onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))}
        />
        <div className="grid grid-cols-2 gap-3">
          <Select
            label={t("Resource")}
            value={form.resourceType}
            onChange={(event) => setForm((prev) => ({ ...prev, resourceType: event.target.value }))}
          >
            <option value="document">{t("Document")}</option>
            <option value="collection">{t("Collection")}</option>
            <option value="*">{t("All resources")}</option>
          </Select>
          <Select
            label={t("Effect")}
            value={form.effect}
            onChange={(event) =>
              setForm((prev) => ({ ...prev, effect: event.target.value as PolicyEffect }))
            }
          >
            <option value="permit">{t("Permit")}</option>
            <option value="deny">{t("Deny")}</option>
          </Select>
        </div>
        <Input
          type="number"
          label={t("Priority")}
          hint={t("Evaluated high to low.")}
          value={form.priority}
          onChange={(event) =>
            setForm((prev) => ({ ...prev, priority: Number(event.target.value) || 0 }))
          }
        />
        <div className="flex flex-wrap gap-3">
          {ACTIONS.map((action) => (
            <label
              key={action}
              className="flex cursor-pointer items-center gap-1.5 text-small text-text"
            >
              <input
                type="checkbox"
                checked={form.actions.includes(action)}
                onChange={() => toggleAction(action)}
              />
              <span className="font-mono">{action}</span>
            </label>
          ))}
        </div>
        <ConditionsEditor
          label={t("Conditions")}
          value={form.conditions}
          onChange={(conditions) => setForm((prev) => ({ ...prev, conditions }))}
        />
        <Switch
          checked={form.isActive}
          onCheckedChange={(isActive) => setForm((prev) => ({ ...prev, isActive }))}
          label={t("Active")}
        />
      </div>
    </Dialog>
  );
}

export function PoliciesSection() {
  const { t } = useTranslation();
  const { data: policies, isLoading } = useAuthPolicies();
  const deletePolicy = useDeleteAuthPolicy();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<AuthPolicy | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<AuthPolicy | null>(null);

  if (isLoading) {
    return <Spinner label={t("Loading")} />;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-end">
        <Button variant="primary" icon={Plus} onClick={() => setCreating(true)}>
          {t("New policy")}
        </Button>
      </div>

      {!policies?.length ? (
        <Empty icon={ShieldCheck} message={t("No policies yet")} />
      ) : (
        <Rows>
          {policies.map((policy) => (
            <Row key={policy.id}>
              <ShieldCheck size={18} aria-hidden className="shrink-0 text-text-tertiary" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-body text-text">
                  {policy.name}
                  {!policy.isActive ? (
                    <span className="ms-2 rounded-sm bg-bg-secondary px-1.5 py-0.5 text-small text-text-tertiary">
                      {t("INACTIVE")}
                    </span>
                  ) : null}
                </p>
                <p className="truncate text-small text-text-tertiary">
                  {policy.resourceType} · {policy.actions.join(", ")} ·{" "}
                  {t("priority {{priority}}", { priority: policy.priority })}
                </p>
              </div>
              <Pill tone={policy.effect === "deny" ? "danger" : "success"}>
                {policy.effect === "deny" ? t("Deny") : t("Permit")}
              </Pill>
              <Menu
                items={[
                  { type: "button", title: t("Edit…"), onClick: () => setEditing(policy) },
                  {
                    type: "button",
                    title: t("Delete…"),
                    dangerous: true,
                    onClick: () => setConfirmDelete(policy),
                  },
                ]}
                trigger={<IconButton icon={DotsThree} aria-label={t("Actions")} />}
              />
            </Row>
          ))}
        </Rows>
      )}

      {creating ? <PolicyDialog onClose={() => setCreating(false)} /> : null}
      {editing ? <PolicyDialog policy={editing} onClose={() => setEditing(null)} /> : null}
      <ConfirmDialog
        open={confirmDelete !== null}
        onOpenChange={(open) => !open && setConfirmDelete(null)}
        title={t("Delete {{name}}?", { name: confirmDelete?.name ?? "" })}
        confirmLabel={t("Delete")}
        cancelLabel={t("Cancel")}
        closeLabel={t("Close")}
        dangerous
        busy={deletePolicy.isPending}
        onConfirm={() => {
          if (!confirmDelete) {
            return;
          }
          deletePolicy.mutate(confirmDelete.id, {
            onSuccess: () => {
              setConfirmDelete(null);
              toast.success(t("Policy deleted"));
            },
          });
        }}
      />
    </div>
  );
}
